/**
 * Device Sync API client (consumer trips shared with the iOS app).
 * See backend docs/device-sync.md
 *
 * PUT envelope matches iOS `buildPutEnvelope`: `{ id, type, body, userId, updatedAt }`.
 * Huma OpenAPI requires all five fields; the server overwrites userId/updatedAt on store.
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

export interface DeviceSyncRecord {
  id: string;
  type: string;
  body: Record<string, unknown>;
  userId?: string;
  updatedAt?: string;
}

/** Canonical PUT envelope — mirrors iOS DeviceSyncAPI.buildPutEnvelope. */
export function buildPutEnvelope(
  id: string,
  type: string,
  body: Record<string, unknown>,
  userId: string,
  updatedAt?: string
): DeviceSyncRecord {
  return {
    id,
    type,
    body,
    userId,
    updatedAt: updatedAt ?? new Date().toISOString(),
  };
}

export async function getDeviceSync(id: string): Promise<DeviceSyncRecord | null> {
  const headers = await buildAuthHeaders();
  const url = `${API_BASE_URL}/devicesync?id=${encodeURIComponent(id)}`;
  const response = await fetch(url, { method: 'GET', headers });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`getDeviceSync failed: ${response.status}`);
  }
  return (await response.json()) as DeviceSyncRecord;
}

/**
 * PUT /devicesync — requires Cognito `userId` (sub) like iOS.
 * `updatedAt` defaults to now; server may overwrite both fields.
 */
export async function putDeviceSync(
  id: string,
  type: string,
  body: Record<string, unknown>,
  userId: string,
  updatedAt?: string
): Promise<void> {
  if (!userId?.trim()) {
    throw new Error('putDeviceSync requires userId (Cognito sub)');
  }
  const headers = await buildAuthHeaders(true);
  const envelope = buildPutEnvelope(id, type, body, userId.trim(), updatedAt);
  const response = await fetch(`${API_BASE_URL}/devicesync`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(envelope),
  });
  if (!response.ok) {
    let detail = `${response.status}`;
    try {
      const err = (await response.json()) as {
        detail?: unknown;
        title?: unknown;
        errors?: unknown;
        message?: unknown;
      };
      if (err?.detail != null) detail = String(err.detail);
      else if (err?.title != null) detail = String(err.title);
      else if (err?.message != null) detail = String(err.message);
      if (err?.errors != null) {
        detail = `${detail} ${JSON.stringify(err.errors)}`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(`putDeviceSync failed: ${detail}`);
  }
}

/** Paginate GET /devicesync/changes until exhausted. */
export async function fetchAllDeviceSyncChanges(): Promise<DeviceSyncRecord[]> {
  const headers = await buildAuthHeaders();
  const all: DeviceSyncRecord[] = [];
  let cursor: string | null = null;

  do {
    const params = new URLSearchParams({ limit: '100' });
    if (cursor) params.set('cursor', cursor);
    const url = `${API_BASE_URL}/devicesync/changes?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`fetchAllDeviceSyncChanges failed: ${response.status}`);
    }
    const json = (await response.json()) as { records?: DeviceSyncRecord[] };
    if (Array.isArray(json.records)) {
      all.push(...json.records);
    }
    const next = response.headers.get('Next-Cursor')?.trim() || null;
    cursor = next && next.length > 0 ? next : null;
  } while (cursor);

  return all;
}

export function catalogRecordId(sub: string): string {
  return `${sub}#catalog`;
}

export function tripRecordId(sub: string, tripId: string): string {
  return `${sub}#trip#${tripId}`;
}

export function cityVisitRecordId(sub: string, cityVisitId: string): string {
  return `${sub}#cityVisit#${cityVisitId}`;
}
